import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import blogImg from "../assests/images/blog-bg.jpg";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import {
  FaSearch,
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);

      try {
        const res = await axios.get("https://jumeiradiscounthotels.com/api/blogs");

        const data = res.data;

        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((b) => b.category)
              .map((b) => [b.category.id, b.category])
          ).values(),
        ];

        setCategories(uniqueCategories);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      .toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory = selectedCategory
      ? blog.category?.id === Number(selectedCategory)
      : true;

    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        <title>
          Travel Blogs & Hotel Guides | Jumeira Discount Hotels
        </title>

        <meta
          name="description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Jumeira Discount Hotels."
        />

        <link
          rel="canonical"
          href="https://jumeiradiscounthotels.com/blogs"
        />

        <meta
          property="og:title"
          content="Travel Blogs & Hotel Guides | Jumeira Discount Hotels"
        />

        <meta
          property="og:description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Jumeira Discount Hotels."
        />

        <meta
          property="og:url"
          content="https://jumeiradiscounthotels.com/blogs"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Travel Blogs & Hotel Guides | Jumeira Discount Hotels"
        />

        <meta
          name="twitter:description"
          content="Read the latest travel blogs, Dubai hotel guides, budget stay tips, affordable hotel recommendations, and travel insights from Jumeira Discount Hotels."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="bg-slate-50">

        {/* HERO */}
        <Hero
          title="Travel Blog"
          subtitle="Discover travel inspiration, Dubai guides and expert tips for your next journey."
          backgroundImage={blogImg}
        />


        {/* MAIN CONTENT */}
        <main className="relative">

          {/* Decorative background */}
          <div className="absolute top-0 left-0 w-80 h-80 bg-[#D4A83F]/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* INTRO */}
            <section className="pt-12 sm:pt-16 lg:pt-20">

              <div className="max-w-3xl">

                <span className="
                  inline-flex
                  items-center
                  gap-2
                  px-4
                  py-2
                  rounded-full
                  bg-[#E8F0F3]
                  text-[#174B5F]
                  text-xs
                  sm:text-sm
                  font-bold
                  uppercase
                  tracking-wider
                ">
                  Travel Inspiration
                </span>

                <h1 className="
                  mt-5
                  text-3xl
                  sm:text-4xl
                  lg:text-5xl
                  font-bold
                  text-slate-900
                  leading-tight
                ">
                  Stories, Guides &
                  <span className="block text-[#174B5F]">
                    Travel Inspiration
                  </span>
                </h1>

                <p className="
                  mt-4
                  text-slate-500
                  text-base
                  sm:text-lg
                  leading-7
                  max-w-2xl
                ">
                  Explore useful travel guides, Dubai hotel recommendations,
                  destination inspiration and practical tips to help you
                  plan your next stay.
                </p>

              </div>

            </section>


            {/* SEARCH / FILTER */}
            <section className="py-8 sm:py-10">

              <div className="
                bg-white
                rounded-[24px]
                border
                border-slate-200
                shadow-[0_15px_40px_rgba(15,23,42,0.07)]
                p-3
                sm:p-4
              ">

                <div className="
                  flex
                  flex-col
                  md:flex-row
                  gap-3
                ">

                  {/* Search */}
                  <div className="relative flex-1">

                    <FaSearch className="
                      absolute
                      left-4
                      top-1/2
                      -translate-y-1/2
                      text-slate-400
                    " />

                    <input
                      type="text"
                      placeholder="Search travel articles..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="
                        w-full
                        pl-11
                        pr-4
                        py-3.5
                        bg-slate-50
                        border
                        border-slate-200
                        rounded-xl
                        outline-none
                        text-sm
                        text-slate-800
                        placeholder:text-slate-400
                        focus:border-[#174B5F]
                        focus:ring-2
                        focus:ring-[#174B5F]/10
                        transition
                      "
                    />

                  </div>


                  {/* Category */}
                  <div className="md:w-64">

                    <select
                      value={selectedCategory}
                      onChange={(e) =>
                        setSelectedCategory(e.target.value)
                      }
                      className="
                        w-full
                        px-4
                        py-3.5
                        bg-slate-50
                        border
                        border-slate-200
                        rounded-xl
                        outline-none
                        text-sm
                        text-slate-700
                        cursor-pointer
                        focus:border-[#174B5F]
                        focus:ring-2
                        focus:ring-[#174B5F]/10
                        transition
                      "
                    >
                      <option value="">
                        All Categories
                      </option>

                      {categories.map((cat) => (
                        <option
                          key={cat.id}
                          value={cat.id}
                        >
                          {cat.name}
                        </option>
                      ))}
                    </select>

                  </div>

                </div>

              </div>

            </section>


            {/* LOADING */}
            {loading ? (

              <div className="
                grid
                grid-cols-1
                md:grid-cols-2
                lg:grid-cols-3
                gap-6
                pb-20
              ">

                {[1, 2, 3, 4, 5, 6].map((i) => (

                  <div
                    key={i}
                    className="
                      bg-white
                      rounded-[24px]
                      overflow-hidden
                      border
                      border-slate-100
                      animate-pulse
                    "
                  >

                    <div className="h-56 bg-slate-200"></div>

                    <div className="p-6">

                      <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>

                      <div className="h-6 bg-slate-200 rounded mb-3"></div>

                      <div className="h-4 bg-slate-200 rounded mb-2"></div>

                      <div className="h-4 bg-slate-200 rounded w-2/3"></div>

                    </div>

                  </div>

                ))}

              </div>

            ) : filteredBlogs.length > 0 ? (

              <>

                {/* FEATURED ARTICLE */}
                <section className="pb-12 sm:pb-16">

                  <Link
                    to={`/blogs/${filteredBlogs[0].slug}`}
                    className="group block"
                  >

                    <div className="
                      relative
                      min-h-[420px]
                      sm:min-h-[480px]
                      lg:min-h-[540px]
                      rounded-[28px]
                      sm:rounded-[32px]
                      overflow-hidden
                      shadow-[0_20px_60px_rgba(15,23,42,0.15)]
                    ">

                      <img
                        src={filteredBlogs[0].thumbnail_image}
                        alt={filteredBlogs[0].title}
                        className="
                          absolute
                          inset-0
                          w-full
                          h-full
                          object-cover
                          group-hover:scale-105
                          transition-transform
                          duration-700
                        "
                      />

                      {/* Overlay */}
                      <div className="
                        absolute
                        inset-0
                        bg-gradient-to-t
                        from-[#061923]
                        via-[#061923]/50
                        to-black/10
                      "></div>


                      {/* Featured label */}
                      <div className="
                        absolute
                        top-5
                        left-5
                        sm:top-7
                        sm:left-7
                      ">

                        <span className="
                          inline-flex
                          items-center
                          gap-2
                          px-4
                          py-2
                          rounded-full
                          bg-white/95
                          backdrop-blur
                          text-[#174B5F]
                          text-xs
                          font-bold
                          uppercase
                          tracking-wider
                          shadow-lg
                        ">
                          Featured Article
                        </span>

                      </div>


                      {/* Content */}
                      <div className="
                        absolute
                        bottom-0
                        left-0
                        right-0
                        p-5
                        sm:p-8
                        lg:p-10
                      ">

                        <span className="
                          inline-flex
                          px-3
                          py-1.5
                          rounded-full
                          bg-[#D4A83F]
                          text-[#071E2C]
                          text-[10px]
                          sm:text-xs
                          font-bold
                          uppercase
                          tracking-wider
                        ">
                          {filteredBlogs[0].category?.name}
                        </span>


                        <h2 className="
                          mt-4
                          text-2xl
                          sm:text-3xl
                          lg:text-4xl
                          font-bold
                          !text-white
                          max-w-4xl
                          leading-tight
                          group-hover:text-[#D4A83F]
                          transition
                        ">
                          {filteredBlogs[0].title}
                        </h2>


                        <p className="
                          mt-3
                          text-sm
                          sm:text-base
                          text-white/75
                          max-w-2xl
                          line-clamp-2
                          leading-6
                        ">
                          {filteredBlogs[0].excerpt}
                        </p>


                        <div className="
                          mt-5
                          flex
                          flex-wrap
                          items-center
                          gap-4
                          text-xs
                          sm:text-sm
                          text-white/60
                        ">

                          <span className="flex items-center gap-2">
                            <FaCalendarAlt />
                            {new Date(
                              filteredBlogs[0].created_at
                            ).toDateString()}
                          </span>

                          <span className="flex items-center gap-2">
                            <FaClock />
                            5 min read
                          </span>

                        </div>

                      </div>

                    </div>

                  </Link>

                </section>


                {/* BLOG HEADER */}
                <div className="
                  flex
                  items-end
                  justify-between
                  gap-4
                  mb-7
                ">

                  <div>

                    <p className="
                      text-xs
                      font-bold
                      uppercase
                      tracking-[0.18em]
                      text-[#D4A83F]
                    ">
                      Latest Articles
                    </p>

                    <h2 className="
                      mt-2
                      text-2xl
                      sm:text-3xl
                      font-bold
                      text-slate-900
                    ">
                      More Travel Stories
                    </h2>

                  </div>

                  <span className="
                    hidden
                    sm:block
                    text-sm
                    text-slate-400
                  ">
                    {filteredBlogs.length} articles
                  </span>

                </div>


                {/* BLOG GRID */}
                <section className="
                  grid
                  grid-cols-1
                  sm:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                  lg:gap-6
                  pb-20
                ">

                  {filteredBlogs.slice(1).map((blog) => (

                    <article
                      key={blog.id}
                      className="
                        group
                        bg-white
                        rounded-[24px]
                        overflow-hidden
                        border
                        border-slate-200
                        shadow-[0_8px_30px_rgba(15,23,42,0.05)]
                        hover:shadow-[0_20px_50px_rgba(15,23,42,0.12)]
                        hover:-translate-y-2
                        transition-all
                        duration-500
                      "
                    >

                      {/* Image */}
                      <Link to={`/blogs/${blog.slug}`}>

                        <div className="
                          relative
                          h-52
                          sm:h-56
                          overflow-hidden
                        ">

                          <img
                            src={blog.thumbnail_image}
                            alt={blog.title}
                            className="
                              w-full
                              h-full
                              object-cover
                              group-hover:scale-105
                              transition
                              duration-700
                            "
                          />

                          <div className="
                            absolute
                            inset-0
                            bg-gradient-to-t
                            from-black/40
                            via-transparent
                            to-transparent
                          "></div>


                          <span className="
                            absolute
                            top-4
                            left-4
                            px-3
                            py-1.5
                            rounded-full
                            bg-white/95
                            backdrop-blur
                            text-[#174B5F]
                            text-[10px]
                            sm:text-xs
                            font-bold
                            shadow-lg
                          ">
                            {blog.category?.name}
                          </span>

                        </div>

                      </Link>


                      {/* Content */}
                      <div className="p-5 sm:p-6">

                        {/* Date */}
                        <div className="
                          flex
                          items-center
                          gap-2
                          text-xs
                          text-slate-400
                        ">

                          <FaCalendarAlt className="text-[#D4A83F]" />

                          {new Date(
                            blog.created_at
                          ).toDateString()}

                        </div>


                        {/* Title */}
                        <Link to={`/blogs/${blog.slug}`}>

                          <h3 className="
                            mt-3
                            text-lg
                            sm:text-xl
                            font-bold
                            text-slate-900
                            line-clamp-2
                            leading-snug
                            group-hover:text-[#174B5F]
                            transition
                          ">
                            {blog.title}
                          </h3>

                        </Link>


                        {/* Excerpt */}
                        <p className="
                          mt-3
                          text-sm
                          text-slate-500
                          leading-6
                          line-clamp-3
                          min-h-[72px]
                        ">
                          {blog.excerpt}
                        </p>


                        {/* Footer */}
                        <div className="
                          mt-5
                          pt-4
                          border-t
                          border-slate-100
                          flex
                          items-center
                          justify-between
                        ">

                          <span className="
                            text-xs
                            text-slate-400
                            flex
                            items-center
                            gap-2
                          ">
                            <FaClock />
                            5 min read
                          </span>


                          <Link
                            to={`/blogs/${blog.slug}`}
                            className="
                              inline-flex
                              items-center
                              gap-2
                              text-sm
                              font-bold
                              text-[#174B5F]
                              group-hover:text-[#D4A83F]
                              transition
                            "
                          >
                            Read Article

                            <FaArrowRight className="
                              text-xs
                              group-hover:translate-x-1
                              transition
                            " />

                          </Link>

                        </div>

                      </div>

                    </article>

                  ))}

                </section>

              </>

            ) : (

              /* EMPTY STATE */
              <div className="
                py-20
                sm:py-28
                text-center
              ">

                <div className="
                  w-16
                  h-16
                  mx-auto
                  rounded-2xl
                  bg-[#E8F0F3]
                  text-[#174B5F]
                  flex
                  items-center
                  justify-center
                  text-xl
                ">
                  <FaSearch />
                </div>

                <h3 className="
                  mt-5
                  text-xl
                  font-bold
                  text-slate-900
                ">
                  No articles found
                </h3>

                <p className="
                  mt-2
                  text-sm
                  text-slate-500
                ">
                  Try changing your search or selecting another category.
                </p>

                <button
                  onClick={() => {
                    setSearch("");
                    setSelectedCategory("");
                  }}
                  className="
                    mt-6
                    px-5
                    py-2.5
                    rounded-xl
                    bg-[#174B5F]
                    text-white
                    text-sm
                    font-semibold
                    hover:bg-[#002B45]
                    transition
                  "
                >
                  Clear Filters
                </button>

              </div>

            )}

          </div>

        </main>

      </div>
    </>
  );
}